/* The "Out of the Menus" Part-2 table logic (designs/navigation-redesign.md
   NR8): pure filter / search / sort / group over a section's records, shared
   by the Projects and portfolio tables. Everything here is a VIEW over
   already-loaded state — nothing mutates, re-files, or fabricates: a record
   whose directoryId no longer names a known folder reads honestly as Not
   filed (the same dirOf rule the picker tree uses), and grouping by state
   never moves a record out of its directory. */
import { RECORD_STATES, type RecordState } from "./projects";
import { subtreeIds, type Directory } from "./directories";

export interface TableRecord {
  id: string;
  name: string;
  state: RecordState;
  directoryId: string | null;
}

export type StateFilter = "all" | RecordState;
/* the rail's selection: "all", "" for Not filed, or a directory id */
export type DirFilter = string;

export interface TableFilters {
  state: StateFilter;
  dir: DirFilter;
  query: string;
}

export function knownDirIds(dirs: readonly Directory[]): Set<string> {
  return new Set(dirs.map((d) => d.id));
}

/* the folder a record honestly reads as filed to: a directoryId the section
   does not know (deleted, foreign) is a gap — Not filed — never a guess */
export function filedTo(r: TableRecord, known: Set<string>): string | null {
  return r.directoryId && known.has(r.directoryId) ? r.directoryId : null;
}

/* the chips' numbers — counted over the rows the chips will slice */
export function stateCounts(rows: readonly TableRecord[]): Record<StateFilter, number> {
  const counts: Record<StateFilter, number> = {
    all: rows.length,
    draft: 0,
    review: 0,
    active: 0,
    archived: 0,
  };
  for (const r of rows) counts[r.state] += 1;
  return counts;
}

const matches = (name: string, q: string): boolean => name.toLowerCase().includes(q);

/* Takes the section's folders rather than just their ids: selecting a folder
   covers its whole subtree (DIR12), which needs the tree, not a flat set. */
export function applyFilters(
  rows: readonly TableRecord[],
  { state, dir, query }: TableFilters,
  dirs: readonly Directory[],
): TableRecord[] {
  const q = query.trim().toLowerCase();
  const known = knownDirIds(dirs);
  /* "all" and Not filed name no folder, so neither has a subtree */
  const scope = dir === "all" || dir === "" ? null : subtreeIds(dir, dirs);
  const inDir = (r: TableRecord): boolean => {
    if (dir === "all") return true;
    const filed = filedTo(r, known);
    if (dir === "") return filed === null;
    return filed !== null && scope!.has(filed);
  };
  return rows.filter(
    (r) => (state === "all" || r.state === state) && inDir(r) && (!q || matches(r.name, q)),
  );
}

export type SortKey = "name" | "state";

/* state sorts in PIPELINE order (section-navigator N6) — draft before review
   before active before archived — never alphabetically */
export function sortRecords(
  rows: readonly TableRecord[],
  key: SortKey,
  asc: boolean,
): TableRecord[] {
  const cmp =
    key === "name"
      ? (a: TableRecord, b: TableRecord) => a.name.localeCompare(b.name)
      : (a: TableRecord, b: TableRecord) =>
          RECORD_STATES.indexOf(a.state) - RECORD_STATES.indexOf(b.state) ||
          a.name.localeCompare(b.name);
  const sorted = [...rows].sort(cmp);
  return asc ? sorted : sorted.reverse();
}

export interface TableGroup {
  /* "" is the Not filed group under folder grouping; the state under state grouping */
  key: string;
  label: string;
  rows: TableRecord[];
}

/* group-by is a LENS: folder → named folders alphabetically with the honest
   Not filed group last; state → pipeline order, empty states omitted */
export function groupRecords(
  rows: readonly TableRecord[],
  groupBy: "folder" | "state",
  dirs: readonly Directory[],
  known: Set<string>,
): TableGroup[] {
  const byName = (a: TableRecord, b: TableRecord): number => a.name.localeCompare(b.name);
  if (groupBy === "state") {
    return RECORD_STATES.map((s) => ({
      key: s,
      label: s,
      rows: rows.filter((r) => r.state === s).sort(byName),
    })).filter((g) => g.rows.length > 0);
  }
  const named = [...dirs]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((d) => ({
      key: d.id,
      label: d.name,
      rows: rows.filter((r) => filedTo(r, known) === d.id).sort(byName),
    }))
    .filter((g) => g.rows.length > 0);
  const unfiled = rows.filter((r) => filedTo(r, known) === null).sort(byName);
  return unfiled.length > 0
    ? [...named, { key: "", label: "Not filed", rows: unfiled }]
    : named;
}
